import React, { useEffect, useState } from 'react';
import {
  Plus,
  Trash,
  ChevronDown,
  FolderOpen,
  FolderClosed,
  ChevronRight,
  Folders,
} from 'lucide-react';
import ConditionsField from 'components/shared/ConditionFields/ConditionFields';
import { COMMON_FIELDS } from 'constants/segmentConstants';
import { useSelector } from 'react-redux';
import { selectAttributes } from 'store/attributes';
import { IAttribute } from 'types/attributes/attributes.types';
import { Condition } from 'types/segments/segments.types';
import { selectSegments, selectSegmentsLoading } from 'store/segments';
import { FieldValidationErrors } from '../../utils/conditionValidation';
import type { FieldOption } from 'components/shared/ConditionFields/ConditionFields';

export type DataSourceOptions = Record<
  string,
  { value: string; label: string }[]
>;

interface ConditionBuilderProps {
  applyMaxHeight?: boolean;
  conditions: { [key: string]: Condition[] };
  onConditionsChange: (conditions: { [key: string]: Condition[] }) => void;
  fieldErrors?: FieldValidationErrors;
  /** Data source options for the condition row (e.g. User Data / Segment). Must be passed from parent. */
  dataSourceOptions: DataSourceOptions;
}

const ConditionBuilder = ({
  applyMaxHeight = true,
  conditions,
  onConditionsChange,
  fieldErrors = {},
  dataSourceOptions,
}: ConditionBuilderProps) => {
  const [conditionType, setConditionType] = useState('and');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const attributes = useSelector(selectAttributes);
  const segments = useSelector(selectSegments);
  const segmentsLoading = useSelector(selectSegmentsLoading);

  useEffect(() => {
    setConditionType(Object.keys(conditions || {})[0] || 'and');
  }, [conditions]);

  // Initialize with default condition if none exist
  useEffect(() => {
    const currentConditions = conditions[conditionType] || [];
    // Only initialize if there are no conditions at all in any key
    const hasAnyConditions = Object.values(conditions || {}).some(
      (conditionArray) =>
        Array.isArray(conditionArray) && conditionArray.length > 0
    );

    if (currentConditions.length === 0 && !hasAnyConditions) {
      onConditionsChange({
        ...conditions,
        [conditionType]: [
          {
            condition: 'segment',
            field: '',
            operator: '',
            value: null,
          },
        ],
      });
    }
  }, [conditionType, conditions, onConditionsChange]);

  const normalizeToOption = (
    item: IAttribute | string
  ): { label: string; value: string; tag?: string } => {
    if (typeof item === 'string') {
      return { label: item, value: item, tag: '' };
    }
    return {
      label: item.name || item.value || '',
      value: item.name || item.label || '',
      tag: item.tag,
    };
  };

  const options: {
    [key: string]: { label: string; value: string; tag?: string }[];
  } = {
    'Contact Fields': COMMON_FIELDS.map(normalizeToOption),
    Attributes: (attributes?.attributes || []).map(normalizeToOption),
    Segments: segmentsLoading
      ? [{ label: 'Loading segments...', value: '', tag: '' }]
      : (segments || [])
          .filter((segment) => segment && segment.name && segment._id)
          .map((segment) => ({
            label: segment.name,
            value: segment._id || '',
            tag: segment.type,
          })),
  };

  const segmentOptions: FieldOption[] = segmentsLoading
    ? [{ label: 'Loading segments...', value: '', tag: '' }]
    : (segments || [])
        .filter((segment) => segment && segment.name && segment._id)
        .map((segment) => ({
          label: segment.name,
          value: segment._id || '',
          tag: segment.type || '',
        }));

  const updateSegmentConditions = (
    updater: (conditions: Condition[]) => Condition[]
  ) => {
    onConditionsChange({
      ...conditions,
      [conditionType]: updater(conditions[conditionType] || []),
    });
  };

  // Helper function to check if a condition is a group
  const isGroupCondition = (condition: Condition): boolean => {
    return condition.condition === 'group';
  };

  // Generate unique ID for groups with level tracking
  const generateGroupId = (
    index: number,
    level: number = 0,
    parentPath: string = ''
  ) => {
    return parentPath
      ? `${parentPath}-group-${index}`
      : `group-${level}-${index}`;
  };

  // Toggle group expansion
  const toggleGroup = (groupId: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  // Render a single condition with support for nested groups
  const renderCondition = (
    condition: Condition,
    index: number,
    level: number = 0,
    parentPath: string = '',
    updateParentCondition?: (
      updater: (condition: Condition) => Condition
    ) => void
  ) => {
    const { value } = condition;

    const updateCondition = (updater: (condition: Condition) => Condition) => {
      if (updateParentCondition) {
        // This is a nested condition, update through parent
        updateParentCondition(updater);
      } else {
        // This is a root level condition
        updateSegmentConditions((prevConditions) => {
          const updatedCondition = updater(prevConditions[index]);
          // Check if condition should be deleted
          if (updatedCondition && (updatedCondition as any).shouldDelete) {
            return prevConditions.filter((_, i) => i !== index);
          }
          const newConditions = [...prevConditions];
          newConditions[index] = updatedCondition;
          return newConditions;
        });
      }
    };

    // If this is a group condition, render it differently
    if (isGroupCondition(condition)) {
      const groupId = generateGroupId(index, level, parentPath);
      const isExpanded = expandedGroups.has(groupId);
      const groupType = (value as any)?.groupType || 'and';
      const groupConditions = (value as any)?.conditions || [];

      const updateGroup = (updater: (groupData: any) => any) => {
        updateCondition((prev) => ({
          ...prev,
          value: updater(prev.value || {}),
        }));
      };

      const addConditionToGroup = () => {
        updateGroup((prev) => ({
          ...prev,
          conditions: [
            ...(prev.conditions || []),
            {
              condition: 'segment',
              field: '',
              operator: '',
              value: null,
            },
          ],
        }));
      };

      const addNestedGroupToGroup = () => {
        updateGroup((prev) => ({
          ...prev,
          conditions: [
            ...(prev.conditions || []),
            {
              condition: 'group',
              field: '',
              operator: '',
              value: {
                groupType: 'and',
                conditions: [],
              },
            },
          ],
        }));
      };

      const updateGroupCondition = (
        conditionIndex: number,
        updater: (condition: Condition) => Condition
      ) => {
        updateGroup((prev) => {
          const updatedCondition = updater(prev.conditions[conditionIndex]);
          // Check if condition should be deleted
          if (updatedCondition && (updatedCondition as any).shouldDelete) {
            return {
              ...prev,
              conditions: (prev.conditions || []).filter(
                (_: Condition, i: number) => i !== conditionIndex
              ),
            };
          }
          return {
            ...prev,
            conditions: (prev.conditions || []).map(
              (c: Condition, i: number) =>
                i === conditionIndex ? updatedCondition : c
            ),
          };
        });
      };

      const deleteGroup = () => {
        if (updateParentCondition) {
          // This is a nested group, signal parent to remove it
          updateParentCondition(() => ({ shouldDelete: true }) as any);
        } else {
          // This is a root level group
          updateSegmentConditions((prevConditions) => {
            const newConditions = [...prevConditions];
            newConditions.splice(index, 1);
            return newConditions;
          });
        }
      };

      return (
        <div
          key={groupId}
          className={`w-full border-2 rounded-lg ${level === 0 ? 'border-primary' : 'border-condition-nested'}`}
        >
          {/* Group Header */}
          <div
            className={`flex items-center justify-between p-2 ${isExpanded ? 'border-b border-b-2' : ''} ${level === 0 ? 'bg-primary/10 border-primary' : 'bg-condition-foreground border-condition-nested'}`}
          >
            <div className="flex items-center gap-2 flex-1">
              <button
                onClick={() => toggleGroup(groupId)}
                className={`p-1 rounded ${level === 0 ? 'hover:bg-primary/20' : 'hover:bg-condition-nested/20'}`}
              >
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              {isExpanded ? (
                <FolderOpen
                  className={`w-4 h-4 ${level === 0 ? 'text-primary' : 'text-condition-nested'}`}
                />
              ) : (
                <FolderClosed
                  className={`w-4 h-4 ${level === 0 ? 'text-primary' : 'text-condition-nested'}`}
                />
              )}
              <select
                className="text-xs font-medium bg-transparent border-none outline-none"
                value={groupType}
                onChange={(e) => {
                  updateGroup((prev) => ({
                    ...prev,
                    groupType: e.target.value,
                  }));
                }}
              >
                <option value="and">All</option>
                <option value="or">Any</option>
              </select>
              <span className="text-xs text-gray-600">
                of the following conditions match
              </span>
              {level > 0 && (
                <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">
                  Nested Group
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={addConditionToGroup}
                className={`p-1 rounded ${level === 0 ? 'text-primary hover:bg-primary/20' : 'text-condition-nested hover:bg-condition-nested/20'}`}
                title="Add Condition"
              >
                <Plus className="w-4 h-4" />
              </button>
              {level === 0 && (
                <button
                  onClick={addNestedGroupToGroup}
                  className="p-1 text-primary hover:bg-primary/20 rounded"
                  title="Add Nested Group"
                >
                  <Folders className="w-4 h-4" />
                </button>
              )}
              <button
                onClick={deleteGroup}
                className="p-2 text-destructive hover:bg-destructive/20 rounded"
                title="Delete Group"
              >
                <Trash className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Group Content */}
          {isExpanded && (
            <div className="p-2 space-y-2">
              {groupConditions.map(
                (groupCondition: Condition, conditionIndex: number) => {
                  return (
                    <React.Fragment
                      key={`${groupId}-condition-${conditionIndex}`}
                    >
                      {renderCondition(
                        groupCondition,
                        conditionIndex,
                        level + 1,
                        groupId,
                        (updater) =>
                          updateGroupCondition(conditionIndex, updater)
                      )}
                      {conditionIndex < groupConditions.length - 1 && (
                        <div className="flex items-center w-full my-1">
                          <div className="flex-1 h-px bg-gray-300"></div>
                          <div className="px-4 py-1.5 mx-3 bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wide rounded-full shadow-sm">
                            {groupType === 'and' ? 'And' : 'Or'}
                          </div>
                          <div className="flex-1 h-px bg-gray-300"></div>
                        </div>
                      )}
                    </React.Fragment>
                  );
                }
              )}

              {groupConditions.length === 0 && (
                <div className="text-center text-gray-500 text-xs py-3">
                  No conditions in this group. Add conditions to get started.
                </div>
              )}
            </div>
          )}
        </div>
      );
    }

    // Leaf condition: use shared ConditionFields
    return (
      <div
        key={`${parentPath}-condition-${index}`}
        className="w-full flex justify-between items-center text-black text-sm rounded-lg duration-200 bg-condition-background px-2 py-2 border border-gray-300 hover:bg-gray-50 hover:border-gray-300 shadow-sm transition-all"
      >
        <div className="flex flex-wrap gap-2 flex-1 items-center min-w-0">
          <ConditionsField
            condition={condition}
            setCondition={(c) => updateCondition(() => c)}
            dataSourceOptions={dataSourceOptions}
            attributeFieldOptions={options}
            segmentOptions={segmentOptions}
            compact
          />
        </div>
        <div className="flex items-center justify-center ml-2 h-full shrink-0">
          <button
            type="button"
            onClick={() => {
              if (updateParentCondition) {
                updateParentCondition(() => ({ shouldDelete: true }) as any);
              } else {
                updateSegmentConditions((prevConditions) => {
                  const newConditions = [...prevConditions];
                  newConditions.splice(index, 1);
                  return newConditions;
                });
              }
            }}
            className="flex items-center justify-center w-8 h-8 text-destructive hover:text-destructive/80 hover:bg-destructive/10 rounded-lg transition-colors"
            title="Delete Condition"
          >
            <Trash className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  };

  return (
    <div
      className={`flex flex-col gap-3 text-xs w-full ${applyMaxHeight ? `max-h-[400px]` : ''} overflow-y-auto`}
    >
      <div className="flex items-center gap-2">
        <select
          className="w-[70px] rounded-md border border-gray-200 bg-gray-50 px-2 py-1 text-xs text-gray-700 outline-none focus:ring-1 focus:ring-primary"
          value={conditionType}
          onChange={(e) => {
            onConditionsChange({
              [e.target.value]: conditions[conditionType] || [],
            });
            setConditionType(e.target.value);
          }}
        >
          <option value="and">All</option>
          <option value="or">Any</option>
        </select>
        <span className="text-xs text-gray-500">
          of the following conditions match
        </span>
      </div>

      <div className="flex flex-col gap-2 w-full">
        {Object.values(conditions || {})
          .flat()
          .map((condition, index) => {
            return (
              <React.Fragment key={`root-condition-${index}`}>
                {renderCondition(condition, index, 0)}
                {index < Object.values(conditions).flat().length - 1 && (
                  <div className="flex items-center w-full my-1">
                    <div className="flex-1 h-px bg-gray-300"></div>
                    <div className="px-4 py-1.5 mx-3 bg-primary/10 border border-primary/20 text-primary text-xs font-semibold uppercase tracking-wide rounded-full shadow-sm">
                      {conditionType === 'and' ? 'And' : 'Or'}
                    </div>
                    <div className="flex-1 h-px bg-gray-300"></div>
                  </div>
                )}
              </React.Fragment>
            );
          })}
      </div>

      <div className="flex gap-2">
        <button
          className="bg-white flex items-center gap-1 text-gray-700 border border-gray-300 hover:bg-gray-50 hover:border-gray-400 text-xs px-3 py-1.5 rounded-lg shadow-sm transition-all"
          onClick={() => {
            updateSegmentConditions((prevConditions) => [
              ...prevConditions,
              {
                condition: 'segment',
                field: '',
                operator: '',
                value: null,
              },
            ]);
          }}
        >
          <Plus className="w-3.5 h-3.5" />
          Add Condition
        </button>

        <button
          className="bg-primary/90 flex items-center gap-1 text-white border border-primary hover:bg-primary hover:border-primary text-xs px-3 py-1.5 rounded-lg shadow-sm transition-all"
          onClick={() => {
            updateSegmentConditions((prevConditions) => [
              ...prevConditions,
              {
                condition: 'group',
                field: '',
                operator: '',
                value: {
                  groupType: 'and',
                  conditions: [],
                },
              },
            ]);
          }}
        >
          <FolderOpen className="w-3.5 h-3.5" />
          Add Group
        </button>
      </div>
    </div>
  );
};

export default ConditionBuilder;
